// Mopper pricing logic — mirrors /api/pricing
// All rates include Swiss VAT 8.1%.

export const HOURLY_RATES_TTC = {
  une_fois: 63.0,
  hebdomadaire: 45.0,
  bi_hebdo: 45.0,
  mensuel: 48.0,
  plus_souvent: 45.0,
};

export const VAT_RATE = 8.1;
export const MIN_HOURS = 2;

// Number of passes per month per frequency (used for monthly estimate)
export const PASSES_PER_MONTH = {
  une_fois: 0, // no monthly estimate for one-off
  hebdomadaire: 4.33,
  bi_hebdo: 2.17,
  mensuel: 1,
  plus_souvent: 8, // 2x/week assumed
};

export const OPTIONS_CATALOG = [
  { id: "vaisselle", label_fr: "Vaisselle", label_de: "Geschirr", label_en: "Dishes", minutes: 30 },
  { id: "frigo", label_fr: "Intérieur frigo", label_de: "Kühlschrank innen", label_en: "Fridge interior", minutes: 30 },
  { id: "cafe", label_fr: "Machine à café", label_de: "Kaffeemaschine", label_en: "Coffee machine", minutes: 15 },
  { id: "four", label_fr: "Four", label_de: "Backofen", label_en: "Oven", minutes: 60 },
  { id: "plaques", label_fr: "Plaques de cuisson", label_de: "Kochplatten", label_en: "Stovetop", minutes: 20 },
  { id: "vapeur_petit", label_fr: "Désinfection vapeur — Petit", label_de: "Dampfdesinfektion — Klein", label_en: "Steam disinfection — Small", minutes: 30 },
  { id: "vapeur_moyen", label_fr: "Désinfection vapeur — Moyen", label_de: "Dampfdesinfektion — Mittel", label_en: "Steam disinfection — Medium", minutes: 45 },
  { id: "vapeur_grand", label_fr: "Désinfection vapeur — Grand", label_de: "Dampfdesinfektion — Gross", label_en: "Steam disinfection — Large", minutes: 60 },
  { id: "profond_details", label_fr: "Nettoyage profond — Détails", label_de: "Tiefenreinigung — Details", label_en: "Deep cleaning — Details", minutes: 45 },
  { id: "profond_complet", label_fr: "Nettoyage profond — Complet", label_de: "Tiefenreinigung — Komplett", label_en: "Deep cleaning — Full", minutes: 90 },
  { id: "balcon_petit", label_fr: "Balcon / terrasse — Petit", label_de: "Balkon / Terrasse — Klein", label_en: "Balcony / terrace — Small", minutes: 30 },
  { id: "balcon_moyen", label_fr: "Balcon / terrasse — Moyen", label_de: "Balkon / Terrasse — Mittel", label_en: "Balcony / terrace — Medium", minutes: 45 },
  { id: "balcon_grand", label_fr: "Balcon / terrasse — Grand", label_de: "Balkon / Terrasse — Gross", label_en: "Balcony / terrace — Large", minutes: 60 },
  { id: "vitres", label_fr: "Vitres (par vitre)", label_de: "Fenster (pro Scheibe)", label_en: "Windows (per pane)", minutes: 20, perUnit: true },
];

export function labelOption(opt, lang) {
  const key = `label_${lang}`;
  return opt[key] || opt.label_fr;
}

export function computePrice({ hours, frequency, options }) {
  const baseMinutes = Math.round((hours || 0) * 60);
  const optionsMinutes = (options || []).reduce((sum, o) => sum + (o.minutes || 0) * (o.quantity || 1), 0);
  const totalMinutes = baseMinutes + optionsMinutes;
  const totalHours = totalMinutes / 60;

  const hourlyTtc = HOURLY_RATES_TTC[frequency] ?? 45;
  const hourlyHt = +(hourlyTtc / (1 + VAT_RATE / 100)).toFixed(2);

  const priceTtc = +(totalHours * hourlyTtc).toFixed(2);
  const priceHt = +(totalHours * hourlyHt).toFixed(2);
  const tvaAmount = +(priceTtc - priceHt).toFixed(2);

  const passes = PASSES_PER_MONTH[frequency] || 0;
  const monthlyTtc = passes > 0 ? +(priceTtc * passes).toFixed(2) : null;

  return {
    total_minutes: totalMinutes,
    total_hours: +totalHours.toFixed(2),
    hourly_rate_ttc: hourlyTtc,
    hourly_rate_ht: hourlyHt,
    price_ht: priceHt,
    price_ttc: priceTtc,
    tva_amount: tvaAmount,
    tva_rate: VAT_RATE,
    monthly_estimate_ttc: monthlyTtc,
    passes_per_month: passes || null,
  };
}

// End-of-lease & end-of-site flat pricing (from Mopper price sheet)
export const END_LEASE_SIZES = [
  { id: "studio", label_fr: "Studio", label_de: "Studio", label_en: "Studio", persons: 1, hours_min: 3, hours_max: 4, ht_min: 300, ht_max: 400, ttc_min: 324.30, ttc_max: 432.40 },
  { id: "2p", label_fr: "2 pièces", label_de: "2-Zimmer", label_en: "2-room", persons: 1, hours_min: 4, hours_max: 5, ht_min: 420, ht_max: 550, ttc_min: 454.00, ttc_max: 594.55 },
  { id: "3p", label_fr: "3 pièces", label_de: "3-Zimmer", label_en: "3-room", persons: 2, hours_min: 3, hours_max: 4, ht_min: 600, ht_max: 700, ttc_min: 648.60, ttc_max: 756.70 },
  { id: "4p", label_fr: "4 pièces", label_de: "4-Zimmer", label_en: "4-room", persons: 2, hours_min: 4, hours_max: 5, ht_min: 700, ht_max: 950, ttc_min: 756.70, ttc_max: 1026.95 },
  { id: "5p", label_fr: "5 pièces", label_de: "5-Zimmer", label_en: "5-room", persons: 3, hours_min: 4, hours_max: 5, ht_min: 950, ht_max: 1300, ttc_min: 1026.95, ttc_max: 1405.30 },
  { id: "maison", label_fr: "Maison", label_de: "Haus", label_en: "House", persons: 3, hours_min: 5, hours_max: 7, ht_min: 1300, ht_max: 3000, ttc_min: 1405.30, ttc_max: 3243.00 },
];

export function computeFlatPrice({ sizeId, options }) {
  const size = END_LEASE_SIZES.find((s) => s.id === sizeId) || END_LEASE_SIZES[0];
  const addOnMinutes = (options || []).reduce((sum, o) => sum + (o.minutes || 0) * (o.quantity || 1), 0);
  // Options are billed at the regular TTC rate (45 CHF/h) since they are add-ons
  const addOnTtc = +((addOnMinutes / 60) * HOURLY_RATES_TTC.une_fois).toFixed(2);
  const ttcMin = +(size.ttc_min + addOnTtc).toFixed(2);
  const ttcMax = +(size.ttc_max + addOnTtc).toFixed(2);
  const ttcMid = +((ttcMin + ttcMax) / 2).toFixed(2);
  const htMid = +(ttcMid / (1 + VAT_RATE / 100)).toFixed(2);
  const totalMinutesMin = size.hours_min * 60 * size.persons + addOnMinutes;
  const totalMinutesMax = size.hours_max * 60 * size.persons + addOnMinutes;
  return {
    total_minutes: Math.round((totalMinutesMin + totalMinutesMax) / 2),
    total_minutes_min: totalMinutesMin,
    total_minutes_max: totalMinutesMax,
    total_hours: +((size.hours_min + size.hours_max) / 2).toFixed(2),
    hourly_rate_ttc: HOURLY_RATES_TTC.une_fois,
    hourly_rate_ht: +(HOURLY_RATES_TTC.une_fois / (1 + VAT_RATE / 100)).toFixed(2),
    price_ht: htMid,
    price_ttc: ttcMid,
    price_ttc_min: ttcMin,
    price_ttc_max: ttcMax,
    tva_amount: +(ttcMid - htMid).toFixed(2),
    tva_rate: VAT_RATE,
    monthly_estimate_ttc: null,
    passes_per_month: null,
    flat: true,
    size_id: size.id,
    persons: size.persons,
  };
}

export function labelSize(size, lang) {
  return size[`label_${lang}`] || size.label_fr;
}

export function formatCHF(v) {
  if (v == null) return "—";
  return new Intl.NumberFormat("fr-CH", { style: "currency", currency: "CHF", minimumFractionDigits: 2 }).format(v);
}

export function formatDuration(totalMinutes) {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h && m) return `${h}h ${m}min`;
  if (h) return `${h}h`;
  return `${m}min`;
}
